import { CALIBRATION_TYPE } from '../../../config/calibration';
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Activity, Check, ChevronRight, CloudOff } from "lucide-react";
import toast from "react-hot-toast";
import { CalibrationService } from "../services";
import { useAuth } from "../../auth";
import { formatSensorType } from "../../../shared/utils/formatters";
import { transformFormDataToAPI } from "../utils/transformFormData";
import {
  clearCalibrationPointFields,
  createBasicFieldsObject,
  createTemperatureFieldsObject,
} from "../startCalibration/utils/fieldHelpers";

import { useSensorTypes } from "../startCalibration/hooks/useSensorTypes";
import { useCalibrationForm } from "../startCalibration/hooks/useCalibrationForm";
import { useDailyEnvironmentalData } from "../startCalibration/hooks/useDailyEnvironmentalData";

import { SensorInfoStep } from "../startCalibration/steps/SensorInfoStep";
import { FrequencyTemperatureStep } from "../startCalibration/steps/FrequencyTemperatureStep";
import { PreviewStep } from "../startCalibration/steps/PreviewStep";
import { getEffectiveModelFamily, isDisplacementCategory } from "../utils/displacementModelFamily";
import type { Sensor } from "../../../types";
import { ROUTES } from '../../../config/links';

const STEPS = ["Sensor Information", "Frequency & Temperature", "Preview & Confirm"];

interface RecalibrationRouteState {
  calibrationType?: typeof CALIBRATION_TYPE.RECALIBRATION;
  /** Full sensor record for the sensor being recalibrated — Step 1 (sensor
   * type/category/model/range) is seeded from this, same "pass the whole
   * record through route state" pattern the View Calibration Details page
   * already uses elsewhere in this app. */
  sensor?: Sensor;
  prefillInputData?: any;
}

/** Builds the Step-1 seed fields from the sensor being recalibrated — no
 * lookup against the sensor-type catalog required, since the sensor's own
 * `sensor_type` already carries everything Step 1 needs. */
const buildSensorInfoSeed = (sensor?: Sensor) => {
  const sensorType = sensor?.sensor_type;
  if (!sensor || !sensorType?.type || !sensorType?.category) return null;

  const isDisplacement = isDisplacementCategory(sensorType.category);
  const modelFamily = isDisplacement ? getEffectiveModelFamily(sensorType) ?? "" : "";
  const range =
    sensorType.measurement_range !== undefined && sensorType.measurement_range !== null
      ? `${sensorType.measurement_range}${sensorType.unit || ""}`
      : "";

  return {
    sensorId: sensor.serial_number ?? "",
    sensorType: sensorType.type,
    category: sensorType.category,
    modelFamily,
    model: sensorType.model ?? "",
    range,
  };
};

const StartCalibrationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, user } = useAuth();
  const canManageEnvironmentalData = hasRole("admin");

  const routeState = (location.state as RecalibrationRouteState | null) ?? null;
  const calibrationType = routeState?.calibrationType;
  const recalibrationSensorId = routeState?.sensor?.id;
  const sensorInfoSeed = useMemo(() => buildSensorInfoSeed(routeState?.sensor), [routeState?.sensor]);

  const [activeStep, setActiveStep] = useState(0);

  const sensorTypes = useSensorTypes(true);
  const { updateCategories, updateModelFamilies, updateModels, updateRanges } = sensorTypes;
  const {
    individualCalibrationData,
    setIndividualCalibrationData,
    calibrationFieldConfig,
    setCalibrationFieldConfig,
    schemaConfig,
    clearAllFields,
  } = useCalibrationForm(sensorInfoSeed, routeState?.prefillInputData, true);

  const { environmentalDataMissing } = useDailyEnvironmentalData(true);

  useEffect(() => {
    if (individualCalibrationData.sensorType) {
      updateCategories(individualCalibrationData.sensorType);
    }
  }, [individualCalibrationData.sensorType, updateCategories]);

  useEffect(() => {
    if (individualCalibrationData.sensorType && individualCalibrationData.category) {
      const category = individualCalibrationData.category;
      if ((category || "").toLowerCase() === "displacement") {
        updateModelFamilies(individualCalibrationData.sensorType, category);
        if (individualCalibrationData.modelFamily) {
          updateModels(
            individualCalibrationData.sensorType,
            category,
            individualCalibrationData.modelFamily
          );
        }
      } else {
        updateModels(individualCalibrationData.sensorType, category);
      }
    }
  }, [
    individualCalibrationData.sensorType,
    individualCalibrationData.category,
    individualCalibrationData.modelFamily,
    updateModelFamilies,
    updateModels,
  ]);

  useEffect(() => {
    if (
      individualCalibrationData.sensorType &&
      individualCalibrationData.category &&
      individualCalibrationData.model
    ) {
      updateRanges(
        individualCalibrationData.sensorType,
        individualCalibrationData.category,
        individualCalibrationData.model
      );
    }
  }, [
    individualCalibrationData.sensorType,
    individualCalibrationData.category,
    individualCalibrationData.model,
    updateRanges,
  ]);

  useEffect(() => {
    if (schemaConfig.fieldConfig) {
      setCalibrationFieldConfig(schemaConfig.fieldConfig);
    } else {
      setCalibrationFieldConfig(null);
    }
  }, [schemaConfig.fieldConfig, setCalibrationFieldConfig]);

  // Frequency fields only: BOL/AOL + all calibration points. Leaves Step-1
  // sensor info and temperature fields untouched.
  const clearFrequencyFields = useCallback(() => {
    const detectedPoints = calibrationFieldConfig?.calibrationPoints || schemaConfig.calibrationPoints;
    const defaultPoints = schemaConfig.calibrationPoints;

    const allIncreasingPoints = Array.from(
      new Set([...detectedPoints.increasing, ...defaultPoints.increasing])
    );
    const allDecreasingPoints = Array.from(
      new Set([...detectedPoints.decreasing, ...defaultPoints.decreasing])
    );
    const allSecondIncreasingPoints = Array.from(
      new Set([...detectedPoints.secondIncreasing, ...defaultPoints.secondIncreasing])
    );

    const updates: any = {
      ...createBasicFieldsObject(),
    };

    const combinedPoints = {
      increasing: allIncreasingPoints,
      decreasing: allDecreasingPoints,
      secondIncreasing: allSecondIncreasingPoints,
    };
    clearCalibrationPointFields(combinedPoints, updates);

    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      ...updates,
    }));
  }, [calibrationFieldConfig, schemaConfig.calibrationPoints, setIndividualCalibrationData]);

  // Temperature fields only: Room T/F, High T/F. Leaves Step-1 sensor info
  // and frequency fields untouched.
  const clearTemperatureFields = useCallback(() => {
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      ...createTemperatureFieldsObject(),
    }));
  }, [setIndividualCalibrationData]);

  // Used when dependent Step-1 selections change (sensor type/category/model/
  // range) — the previously entered frequency+temperature, and any
  // category-specific additional field (e.g. equipment certificate no.), no
  // longer apply.
  const clearCalibrationFields = useCallback(() => {
    clearFrequencyFields();
    clearTemperatureFields();
    setIndividualCalibrationData((prev: any) => ({
      ...prev,
      calibrationFactor: "",
      temperatureCoefficient: "",
      baselineFrequency: "",
      ambientTemp: "",
      atmPressure: "",
      dwtTraceable: "",
    }));
  }, [clearFrequencyFields, clearTemperatureFields, setIndividualCalibrationData]);

  const prevDependenciesRef = useRef<{
    sensorType?: string;
    category?: string;
    model?: string;
    range?: string;
  }>({});

  useEffect(() => {
    const current = {
      sensorType: individualCalibrationData.sensorType,
      category: individualCalibrationData.category,
      model: individualCalibrationData.model,
      range: individualCalibrationData.range,
    };

    const prev = prevDependenciesRef.current;

    const hasChanged =
      prev.sensorType !== undefined &&
      (current.sensorType !== prev.sensorType ||
        current.category !== prev.category ||
        current.model !== prev.model ||
        current.range !== prev.range);

    if (hasChanged) {
      clearCalibrationFields();
    }

    prevDependenciesRef.current = current;
  }, [
    individualCalibrationData.sensorType,
    individualCalibrationData.category,
    individualCalibrationData.model,
    individualCalibrationData.range,
    clearCalibrationFields,
  ]);

  // Step 1 -> Step 2 gate: sensor info fields only (mirrors the modal's
  // submit-button checks, minus BOL — BOL belongs to step 2).
  const missingRequiredAdditionalFields = calibrationFieldConfig?.additionalFields?.some((field: any) => {
    if (field.required) {
      const shouldShow = !field.condition || field.condition(individualCalibrationData);
      if (shouldShow) {
        const value = individualCalibrationData[field.key];
        return !value || value.toString().trim() === "";
      }
    }
    return false;
  }) ?? false;

  const isSensorInfoValid =
    !!individualCalibrationData.sensorId &&
    !!individualCalibrationData.sensorType &&
    !!individualCalibrationData.category &&
    !(
      (individualCalibrationData.category || "").toLowerCase() === "displacement" &&
      !individualCalibrationData.modelFamily
    ) &&
    !!individualCalibrationData.model &&
    !!individualCalibrationData.range &&
    !missingRequiredAdditionalFields;

  const handleNextFromSensorInfo = () => {
    if (environmentalDataMissing) {
      toast.error("Today's environmental data has not been entered yet. Please contact an admin.");
      return;
    }
    if (!isSensorInfoValid) {
      toast.error("Please fill in all required sensor information fields.");
      return;
    }
    setActiveStep(1);
  };

  const handleNextFromFrequencyTemperature = () => {
    if (!individualCalibrationData.BOL) {
      toast.error("Please fill in BOL before continuing.");
      return;
    }
    setActiveStep(2);
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const handleCancel = () => {
    navigate(ROUTES.CALIBRATIONS);
  };

  const handleProcessData = async () => {
    try {
      if (environmentalDataMissing) {
        toast.error("Today's environmental data has not been entered yet. Please contact an admin.");
        return;
      }

      if (!isSensorInfoValid || !individualCalibrationData.BOL) {
        toast.error("Please fill in all required fields.");
        return;
      }

      const loadingToast = toast.loading("Calibrating sensor...");

      try {
        const apiData = transformFormDataToAPI(
          individualCalibrationData,
          (calibrationType || CALIBRATION_TYPE.INITIAL)
        );

        if (calibrationType === CALIBRATION_TYPE.RECALIBRATION && recalibrationSensorId) {
          let createdBy = "system";
          try {
            createdBy = user?.email || user?.username || "system";
          } catch {
            // keep default
          }

          const recalibrationPayload = {
            sensor_id: recalibrationSensorId,
            calibration_type: CALIBRATION_TYPE.RECALIBRATION,
            input_data: {
              ...apiData.input_data,
              raw_frequency: apiData.input_data.raw_frequency as any,
            },
            created_by: createdBy,
          };

          await CalibrationService.create(recalibrationPayload);
        } else {
          await CalibrationService.calibrateSensor(apiData);
        }

        clearAllFields();

        toast.dismiss(loadingToast);
        const successMessage =
          calibrationType === CALIBRATION_TYPE.RECALIBRATION
            ? "Re-calibration created successfully!"
            : `Calibration completed successfully! Sensor ${individualCalibrationData.sensorId} has been calibrated.`;
        toast.success(successMessage);

        navigate(ROUTES.CALIBRATIONS);
      } catch (apiError: any) {
        toast.dismiss(loadingToast);

        const detail = apiError.response?.data?.detail;
        let serverMessage: string | undefined;
        if (detail && typeof detail === "object") {
          serverMessage = detail.error_details || detail.error;
        } else if (typeof detail === "string") {
          serverMessage = detail;
        }
        const errorToastOptions = { duration: 8000 };

        if (calibrationType === CALIBRATION_TYPE.RECALIBRATION) {
          if (apiError.response?.status === 404) {
            toast.error(serverMessage || "Sensor not found for recalibration", errorToastOptions);
          } else if (apiError.response?.status === 400) {
            toast.error(serverMessage || "Invalid calibration data", errorToastOptions);
          } else if (apiError.response?.status === 403) {
            toast.error(serverMessage || "You do not have permission to create recalibration", errorToastOptions);
          } else if (apiError.response?.status === 500) {
            toast.error(serverMessage || "Server error occurred while creating recalibration", errorToastOptions);
          } else {
            toast.error(
              serverMessage || apiError.message || "Failed to create recalibration. Please try again.",
              errorToastOptions
            );
          }
        } else {
          toast.error(
            serverMessage || apiError.message || "Failed to calibrate sensor. Please check your data and try again.",
            errorToastOptions
          );
        }
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center space-x-2 text-sm text-gray-500 border-b border-gray-200 pb-4">
        <button
          onClick={handleCancel}
          className="hover:text-gray-700 transition-colors cursor-pointer"
          title="Back to Calibrations Management"
        >
          Calibrations Management
        </button>
        <ChevronRight className="h-4 w-4" />
        <span className="text-primary-600 font-medium">
          {calibrationType === CALIBRATION_TYPE.RECALIBRATION ? "Start Re-calibration" : "Start New Calibration"}
        </span>
      </div>

      {/* Page Header */}
      <div className="flex items-start gap-3">
        <Activity className="h-8 w-8 flex-shrink-0 text-primary-600 mt-0.5" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            {calibrationType === CALIBRATION_TYPE.RECALIBRATION ? "Start Re-calibration" : "Start New Calibration"}
          </h1>
          <p className="mt-1 text-sm text-gray-500">Enter sensor information and readings, then review before submitting</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="card p-4 sm:p-6">
        <ol className="mx-auto flex items-center justify-center">
          {STEPS.map((label, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;
            return (
              <li key={label} className="flex items-center">
                <div className="flex flex-shrink-0 items-center gap-2.5">
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                      isCompleted
                        ? "bg-primary-600 text-white"
                        : isActive
                        ? "border-2 border-primary-600 text-primary-600"
                        : "border-2 border-gray-300 text-gray-400"
                    }`}
                  >
                    {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
                  </div>
                  <span
                    className={`whitespace-nowrap text-sm font-medium ${
                      isActive || isCompleted ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {label}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`mx-6 h-0.5 w-24 sm:w-40 ${isCompleted ? "bg-primary-600" : "bg-gray-200"}`}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Step content */}
      <div className="card p-4 sm:p-6 space-y-6">
        {environmentalDataMissing ? (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <CloudOff className="h-5 w-5 flex-shrink-0 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Today's environmental data has not been entered.
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Ambient temperature, humidity, atmospheric pressure, and weather condition must be set
                for today before a calibration can be started.{" "}
                {canManageEnvironmentalData ? (
                  <button
                    type="button"
                    onClick={() => navigate(ROUTES.MANAGE_DAILY_ENV_DATA)}
                    className="font-medium underline hover:text-amber-900"
                  >
                    Set it now
                  </button>
                ) : (
                  "Please ask an admin to set it."
                )}
              </p>
            </div>
          </div>
        ) : activeStep === 0 ? (
          <SensorInfoStep
            individualCalibrationData={individualCalibrationData}
            setIndividualCalibrationData={setIndividualCalibrationData}
            sensorTypes={sensorTypes}
            calibrationFieldConfig={calibrationFieldConfig}
          />
        ) : activeStep === 1 ? (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {formatSensorType(individualCalibrationData.sensorType)} Sensor Specifications
            </h3>

            <FrequencyTemperatureStep
              individualCalibrationData={individualCalibrationData}
              setIndividualCalibrationData={setIndividualCalibrationData}
              schemaConfig={schemaConfig}
              onClearFrequencyFields={clearFrequencyFields}
              onClearTemperatureFields={clearTemperatureFields}
            />
          </div>
        ) : (
          <PreviewStep
            individualCalibrationData={individualCalibrationData}
            calibrationFieldConfig={calibrationFieldConfig}
            schemaConfig={schemaConfig}
          />
        )}

        {/* Actions */}
        {!environmentalDataMissing && (
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <div>
              {activeStep > 0 && (
                <button onClick={handleBack} className="btn-secondary">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              <button onClick={handleCancel} className="btn-secondary">
                Cancel
              </button>
              {activeStep === 0 ? (
                <button
                  onClick={handleNextFromSensorInfo}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
                  disabled={!isSensorInfoValid}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </button>
              ) : activeStep === 1 ? (
                <button
                  onClick={handleNextFromFrequencyTemperature}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
                  disabled={!individualCalibrationData.BOL}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </button>
              ) : (
                <button
                  onClick={handleProcessData}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
                  disabled={!individualCalibrationData.BOL}
                >
                  Process Data
                  <ChevronRight className="h-4 w-4 ml-2" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartCalibrationPage;
